import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";

import { cn } from "@/lib/cn";

/**
 * A slider component that allows users to select a value from a range.
 * @param {Object} props
 * @param {string} [name] - The name of the slider for form submission
 * @param {boolean} [disabled] - Whether the slider is disabled
 * @param {React.AriaAttributes['aria-orientation']} [orientation] - The orientation of the slider ('horizontal' | 'vertical')
 * @param {'ltr' | 'rtl'} [dir] - The text direction
 * @param {number} [min] - The minimum value
 * @param {number} [max] - The maximum value
 * @param {number} [step] - The stepping interval
 * @param {number} [minStepsBetweenThumbs] - The minimum steps between multiple thumbs
 * @param {number[]} [value] - The controlled value
 * @param {number[]} [defaultValue] - The default value
 * @param {(value: number[]) => void} [onValueChange] - Callback when value changes
 * @param {(value: number[]) => void} [onValueCommit] - Callback when value changes is committed
 * @param {boolean} [inverted] - Whether the scale is inverted
 * @param {string} [form] - The form ID this slider belongs to
 */

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    thumbClassName?: string;
    rangeClassName?: string;
    trackClassName?: string;
  }
>(
  (
    { className, thumbClassName, rangeClassName, trackClassName, ...props },
    ref,
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "tw-relative tw-flex tw-w-full tw-touch-none tw-select-none tw-items-center",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          "tw-relative tw-h-1.5 tw-w-full tw-grow tw-overflow-hidden tw-rounded-full tw-bg-[#E7E6EC]",
          trackClassName,
        )}
      >
        <SliderPrimitive.Range
          className={cn(
            "tw-absolute tw-h-full tw-rounded-full tw-bg-primary-base",
            rangeClassName,
          )}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className={cn(
          "tw-block tw-size-4 tw-cursor-pointer tw-rounded-full tw-border tw-border-primary-base tw-bg-white tw-shadow-none tw-transition-[box-shadow] hover:tw-outline-none hover:tw-ring-2 hover:tw-ring-gray-300 hover:tw-ring-offset-0 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-200 focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50",
          thumbClassName,
        )}
      />
    </SliderPrimitive.Root>
  ),
);
Slider.displayName = SliderPrimitive.Root.displayName;

export default Slider;
